import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, tap } from 'rxjs';
import { UserInfo } from 'src/app/models/userInfo';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';
import { SKIP_GLOBAL_ERROR } from '../error-handler/error-handler';
import { JwtService } from '../jwt/jwt-service';
import { ModulesService } from '../modules/modules-service';
import { NotificationService } from '../notification/notification-service';
import { PermissionService } from '../user/permission-service';
import { AuthStateService } from './auth-state-service';
import { SessionTimeoutService } from './session-timeout-service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private modulesService = inject(ModulesService);
  private notificationService = inject(NotificationService);
  private permissionService = inject(PermissionService);
  private authStateService = inject(AuthStateService);
  private jwtService = inject(JwtService);
  private sessionTimeoutService = inject(SessionTimeoutService);
  private cache = inject(ApiCacheService);

  private baseURL: string = environment.HTTP;
  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  user$ = this.userSubject.asObservable();

  userPermsCached: any[] = [];

  apiLogin = `${this.baseURL}/Login/Login`;
  apiLogout = `${this.baseURL}/Login/Logout`;
  apiLoginOtp = `${this.baseURL}/Login/GenerateLoginOtp`;
  apiOtpVerify = `${this.baseURL}/Login/VerifyLoginOtp`;
  apiEmailOtp = `${this.baseURL}/Login/GenerateResetPasswordOtp`;
  apiRPOtpVerify = `${this.baseURL}/Login/VerifyResetPasswordOtp`;
  apiResetPass = `${this.baseURL}/Login/ResetPassword`;
  apiRegVerifyEmail = `${this.baseURL}/Login/VerifyWorkEmail`;
  apiRegVerifyOtp = `${this.baseURL}/Login/VerifyEmailOtp`;
  apiTotpVerify = `${this.baseURL}/Login/VerifyLoginTotp`;
  apiVerifyRecoveryCode = `${this.baseURL}/Login/VerifyRecoveryCode`;
  apiSelfReg = `${this.baseURL}/Login/SelfRegister`;
  apiDepartmentDropdown = `${this.baseURL}/Login/GetDepartmentDropdown`;

  apiVersion = `/version`;

  constructor() {
    this.authStateService.logout$.subscribe(() => {
      this.Logout().subscribe({
        next: () => this.clearLogout(),
        error: () => this.clearLogout() // clear locally even if API fails
      });
    });

    this.sessionTimeoutService.sessionExpired$.subscribe(() => {
      this.Logout().subscribe({
        next: () => {
          this.clearLogout();
        }
      });
    });
  }

  //#region Login

  Login(data: any) {
    return this.http
      .post(this.apiLogin, data, {
        withCredentials: true,
        context: true ? new HttpContext().set(SKIP_GLOBAL_ERROR, true) : undefined
      })
      .pipe(
        tap(() => {
          this.cache.clear();
        })
      );
  }

  GenerateLoginOTP(data: any) {
    return this.http.post(this.apiLoginOtp, data);
  }

  VerifyLoginOTP(data: any) {
    return this.http.post(this.apiOtpVerify, data, {
      withCredentials: true
    });
  }

  Logout() {
    return this.http.post(this.apiLogout, null);
  }

  clearLogout() {
    this.clearUser();
    this.modulesService.clearSelectedModule();
    this.modulesService.clearModules();
    this.permissionService.clearUserPermsCache();
    this.jwtService.removeToken();
    this.notificationService.stopConnection();
    this.sessionTimeoutService.destroy();
    localStorage.removeItem('banner');
    this.router.navigate(['/login']);
  }

  //#region 2FA
  VerifyLoginTotp(data: any) {
    return this.http.post(this.apiTotpVerify, data, {
      withCredentials: true
    });
  }

  VerifyRecoveryCode(data: any) {
    return this.http.post(this.apiVerifyRecoveryCode, data, {
      withCredentials: true
    });
  }

  //#region Forgot Password
  GenerateRPOtp(data: any) {
    return this.http.post(this.apiEmailOtp, data);
  }

  VerifyRPOTP(data: any) {
    return this.http.post(this.apiRPOtpVerify, data);
  }

  ResetPassword(data: any) {
    return this.http.post(this.apiResetPass, data);
  }

  //#region Register
  VerifyEmail(data: any) {
    return this.http.post(this.apiRegVerifyEmail, data);
  }

  VerifyEmailOtp(data: any) {
    return this.http.post(this.apiRegVerifyOtp, data);
  }

  SelfRegister(data: any) {
    return this.http.post(this.apiSelfReg, data);
  }

  //#region User Info
  setUser(user: UserInfo) {
    localStorage.setItem('user', JSON.stringify(user));
    this.userSubject.next(user);
  }

  getUser(): UserInfo | null {
    return this.userSubject.value;
  }

  clearUser() {
    localStorage.removeItem('user');
    this.userSubject.next(null);
  }

  loadUserFromStorage() {
    const stored = localStorage.getItem('user');
    if (stored) {
      this.userSubject.next(JSON.parse(stored));
    }
  }

  //#region Main Entity
  DepartmentDropdown() {
    return this.cache.get(`apiDepartmentDropdown`, () => this.http.get<any>(this.apiDepartmentDropdown));
  }

  //#region Version
  GetVersion() {
    const versionUrl = this.baseURL.replace('/api', ''); // removes the /api part
    return this.cache.get(`${versionUrl} + apiVersion`, () => this.http.get<any>(`${versionUrl}` + this.apiVersion));
  }

  //#region Nav Bar
  apiHeaderGetCountryDropdown = `${this.baseURL}/Login/GetHeaderCountryDropdown`;
  apiHeaderGetEntityDropdown = `${this.baseURL}/Login/GetHeaderEntityDropdown`;
  apiHeaderGetLanguageDropdown = `${this.baseURL}/Login/GetHeaderLanguageDropdown`;

  apiSwitchCountry = `${this.baseURL}/Login/SwitchCountry`;
  apiSwitchLanguage = `${this.baseURL}/Login/SwitchLanguage`;

  GetHeaderCountry() {
    return this.http.get<any>(this.apiHeaderGetCountryDropdown);
  }

  GetHeaderEntity(id: number) {
    return this.cache.get(`apiHeaderGetEntityDropdown=${id}`, () =>
      this.http.get<any>(this.apiHeaderGetEntityDropdown, { params: { scopeId: id } })
    );
  }

  GetHeaderLanguage() {
    return this.cache.get(`apiHeaderGetLanguageDropdown`, () => this.http.get<any>(this.apiHeaderGetLanguageDropdown));
  }

  SwitchCountry(data: any) {
    return this.http.post(this.apiSwitchCountry, data);
  }

  SwitchLanguage(data: any) {
    return this.http.post(this.apiSwitchLanguage, data);
  }

  //#region Rerouting
  on403() {
    this.router.navigate(['/permission-denied']);
  }

  on503() {
    this.router.navigate(['/maintenance']);
  }

  //#region Language Dropdown
  private langList = signal<any[]>([]);

  getAvailableLanguages() {
    return firstValueFrom(this.GetHeaderLanguage()).then((response: any) => {
      this.langList.set(response.data);
    });
  }

  async getLanguageList() {
    if (this.langList().length === 0) {
      await this.getAvailableLanguages();
    }
    return this.langList();
  }
}
